import React, { useState, useRef } from 'react';
import { uploadProfileImage } from '../utils/imageUpload';
import { UserCircleIcon } from '@heroicons/react/24/outline';

const ImageUpload = ({ 
  currentImage, 
  onUploadSuccess, 
  userId,
  className = '',
  size = 'w-24 h-24' // Default size for profile images
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError('');
    setUploadProgress(0);
    
    try {
      console.log('Starting image upload for file:', file.name, 'Size:', file.size, 'Type:', file.type);
      console.log('User ID:', userId);
      
      await uploadProfileImage({
        file,
        userId,
        onProgress: (progress) => {
          console.log('Upload progress:', progress + '%');
          setUploadProgress(progress);
        },
        onError: (err) => {
          console.error('Upload error:', err);
          setError(err);
        },
        onSuccess: (url, imageData) => {
          console.log('Upload successful, URL:', url);
          console.log('Image data:', imageData);
          onUploadSuccess(url, imageData);
          setUploadProgress(100);
        }
      });
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div 
        className={`relative ${size} rounded-full overflow-hidden bg-gray-100 group cursor-pointer`}
        onClick={() => fileInputRef.current?.click()}
      >
        {currentImage ? (
          <img 
            src={currentImage}
            alt="Profile" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '';
              e.target.className = 'hidden';
            }}
          />
        ) : (
          <UserCircleIcon className="w-full h-full text-gray-400" />
        )}

        {/* Overlay with upload button */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
          <div
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded-full p-2 shadow-lg"
            title="Upload new photo"
          >
            {isUploading ? (
              <div className="w-6 h-6 border-2 border-t-cerulean rounded-full animate-spin" />
            ) : (
              <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            )}
          </div>
        </div>

        {/* Upload progress overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="w-8 h-8 border-2 border-t-white rounded-full animate-spin mx-auto mb-2" />
              <div className="text-xs">{uploadProgress}%</div>
            </div>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />

      {/* Error message */}
      {error && (
        <div className="absolute -bottom-8 left-0 right-0 text-red-500 text-xs text-center bg-white p-1 rounded shadow">
          {error}
        </div>
      )}

      {/* Upload progress bar */}
      {isUploading && (
        <div className="absolute -bottom-6 left-0 right-0 h-1 bg-gray-200 rounded">
          <div 
            className="h-full bg-cerulean rounded transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
